import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const GroupCreationForm = ({ onCreateGroup, supervisors, currentLanguage, t }) => {
  const [formData, setFormData] = useState({
    name: '',
    type: '',
    capacity: '',
    supervisorId: '',
    departureDate: '',
    returnDate: '',
    makkahHotel: '',
    madinahHotel: '',
    flightNumber: ''
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const labels = {
    fr: {
      title: 'Créer un nouveau groupe',
      groupName: 'Nom du groupe',
      groupNamePlaceholder: 'Ex: Groupe Omra Ramadan 2026',
      pilgrimageType: 'Type de pèlerinage',
      pilgrimageTypePlaceholder: 'Sélectionner le type',
      capacity: 'Capacité maximale',
      capacityPlaceholder: 'Nombre de pèlerins',
      supervisor: 'Superviseur',
      supervisorPlaceholder: 'Sélectionner un superviseur',
      departureDate: 'Date de départ',
      returnDate: 'Date de retour',
      makkahHotel: 'Hôtel à La Mecque',
      makkahHotelPlaceholder: 'Nom de l\'hôtel',
      madinahHotel: 'Hôtel à Médine',
      madinahHotelPlaceholder: 'Nom de l\'hôtel',
      flightNumber: 'Numéro de vol',
      flightNumberPlaceholder: 'Ex: AT123',
      createGroup: 'Créer le groupe',
      cancel: 'Annuler',
      requiredField: 'Ce champ est requis',
      minCapacity: 'La capacité minimale est de 10 pèlerins',
      maxCapacity: 'La capacité maximale est de 500 pèlerins',
      invalidDate: 'La date de retour doit être après la date de départ'
    },
    ar: {
      title: 'إنشاء مجموعة جديدة',
      groupName: 'اسم المجموعة',
      groupNamePlaceholder: 'مثال: مجموعة عمرة رمضان 2026',
      pilgrimageType: 'نوع الحج',
      pilgrimageTypePlaceholder: 'اختر النوع',
      capacity: 'السعة القصوى',
      capacityPlaceholder: 'عدد الحجاج',
      supervisor: 'المشرف',
      supervisorPlaceholder: 'اختر المشرف',
      departureDate: 'تاريخ المغادرة',
      returnDate: 'تاريخ العودة',
      makkahHotel: 'فندق مكة',
      makkahHotelPlaceholder: 'اسم الفندق',
      madinahHotel: 'فندق المدينة',
      madinahHotelPlaceholder: 'اسم الفندق',
      flightNumber: 'رقم الرحلة',
      flightNumberPlaceholder: 'مثال: AT123',
      createGroup: 'إنشاء المجموعة',
      cancel: 'إلغاء',
      requiredField: 'هذا الحقل مطلوب',
      minCapacity: 'الحد الأدنى للسعة هو 10 حجاج',
      maxCapacity: 'الحد الأقصى للسعة هو 500 حاج',
      invalidDate: 'يجب أن يكون تاريخ العودة بعد تاريخ المغادرة'
    }
  };

  const l = labels?.[currentLanguage];

  const pilgrimageTypeOptions = [
    { value: 'Omra', label: t('groups.omra') },
    { value: 'Hajj', label: t('groups.hajj') }
  ];

  const supervisorOptions = supervisors?.map(sup => ({
    value: sup?.id,
    label: sup?.name
  }));

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.name?.trim()) newErrors.name = l?.requiredField;
    if (!formData?.type) newErrors.type = l?.requiredField;
    if (!formData?.capacity) {
      newErrors.capacity = l?.requiredField;
    } else {
      const cap = parseInt(formData?.capacity);
      if (cap < 10) newErrors.capacity = l?.minCapacity;
      if (cap > 500) newErrors.capacity = l?.maxCapacity;
    }
    if (!formData?.supervisorId) newErrors.supervisorId = l?.requiredField;
    if (!formData?.departureDate) newErrors.departureDate = l?.requiredField;
    if (!formData?.returnDate) newErrors.returnDate = l?.requiredField;
    if (formData?.departureDate && formData?.returnDate) {
      if (new Date(formData.returnDate) <= new Date(formData.departureDate)) {
        newErrors.returnDate = l?.invalidDate;
      }
    }
    if (!formData?.makkahHotel?.trim()) newErrors.makkahHotel = l?.requiredField;
    if (!formData?.flightNumber?.trim()) newErrors.flightNumber = l?.requiredField;

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      await onCreateGroup(formData);
      setFormData({
        name: '',
        type: '',
        capacity: '',
        supervisorId: '',
        departureDate: '',
        returnDate: '',
        makkahHotel: '',
        madinahHotel: '',
        flightNumber: ''
      });
    } catch (error) {
      console.error('Error creating group:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReset = () => {
    setFormData({
      name: '',
      type: '',
      capacity: '',
      supervisorId: '',
      departureDate: '',
      returnDate: '',
      makkahHotel: '',
      madinahHotel: '',
      flightNumber: ''
    });
    setErrors({});
  };

  return (
    <div className="bg-card border border-border rounded-lg shadow-warm p-4 md:p-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
          <Icon name="Plus" size={20} className="text-primary" />
        </div>
        <h2 className="text-xl md:text-2xl font-semibold text-foreground">{l?.title}</h2>
      </div>
      <form onSubmit={handleSubmit} className="space-y-4">
        <Input
          label={l?.groupName}
          type="text"
          placeholder={l?.groupNamePlaceholder}
          value={formData?.name}
          onChange={(e) => handleChange('name', e?.target?.value)}
          error={errors?.name}
          required
        />

        <Select
          label={l?.pilgrimageType}
          placeholder={l?.pilgrimageTypePlaceholder}
          options={pilgrimageTypeOptions}
          value={formData?.type}
          onChange={(value) => handleChange('type', value)}
          error={errors?.type}
          required
        />

        <Input
          label={l?.capacity}
          type="number"
          placeholder={l?.capacityPlaceholder}
          value={formData?.capacity}
          onChange={(e) => handleChange('capacity', e?.target?.value)}
          error={errors?.capacity}
          min="10"
          max="500"
          required
        />

        <Select
          label={l?.supervisor}
          placeholder={l?.supervisorPlaceholder}
          options={supervisorOptions}
          value={formData?.supervisorId}
          onChange={(value) => handleChange('supervisorId', value)}
          error={errors?.supervisorId}
          searchable
          required
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label={l?.departureDate}
            type="date"
            value={formData?.departureDate}
            onChange={(e) => handleChange('departureDate', e?.target?.value)}
            error={errors?.departureDate}
            required
          />

          <Input
            label={l?.returnDate}
            type="date"
            value={formData?.returnDate}
            onChange={(e) => handleChange('returnDate', e?.target?.value)}
            error={errors?.returnDate}
            required
          />
        </div>

        <Input
          label={l?.makkahHotel}
          type="text"
          placeholder={l?.makkahHotelPlaceholder}
          value={formData?.makkahHotel}
          onChange={(e) => handleChange('makkahHotel', e?.target?.value)}
          error={errors?.makkahHotel}
          required
        />

        <Input
          label={l?.madinahHotel}
          type="text"
          placeholder={l?.madinahHotelPlaceholder}
          value={formData?.madinahHotel}
          onChange={(e) => handleChange('madinahHotel', e?.target?.value)}
        />

        <Input
          label={l?.flightNumber}
          type="text"
          placeholder={l?.flightNumberPlaceholder}
          value={formData?.flightNumber}
          onChange={(e) => handleChange('flightNumber', e?.target?.value)}
          error={errors?.flightNumber}
          required
        />

        <div className="flex gap-3 pt-4">
          <Button
            type="submit"
            variant="default"
            iconName="Check"
            iconPosition="left"
            loading={isSubmitting}
            fullWidth
          >
            {l?.createGroup}
          </Button>
          <Button
            type="button"
            variant="outline"
            iconName="X"
            iconPosition="left"
            onClick={handleReset}
            disabled={isSubmitting}
          >
            {l?.cancel}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default GroupCreationForm;